import React, { createContext, useContext, useEffect, useState } from 'react';
import { getWhiteLabelConfig, WhiteLabelConfig } from '../api/whiteLabel';

interface WhiteLabelContextType {
  config: WhiteLabelConfig | null;
  reload: () => void;
}

const WhiteLabelContext = createContext<WhiteLabelContextType>({ config: null, reload: () => {} });

export const useWhiteLabel = () => useContext(WhiteLabelContext);

// Utilitaires couleur
const hexToRgb = (hex: string) => ({
  r: parseInt(hex.slice(1, 3), 16),
  g: parseInt(hex.slice(3, 5), 16),
  b: parseInt(hex.slice(5, 7), 16),
});

const lighten = (hex: string, amount: number) => {
  const { r, g, b } = hexToRgb(hex);
  const f = (c: number) => Math.min(255, c + Math.round((255 - c) * amount)).toString(16).padStart(2, '0');
  return `#${f(r)}${f(g)}${f(b)}`;
};

const darken = (hex: string, amount: number) => {
  const { r, g, b } = hexToRgb(hex);
  const f = (c: number) => Math.max(0, c - Math.round(c * amount)).toString(16).padStart(2, '0');
  return `#${f(r)}${f(g)}${f(b)}`;
};

const applyColors = (primary: string) => {
  const pLight = lighten(primary, 0.9);
  const pDark  = darken(primary, 0.15);

  let el = document.getElementById('wl-styles');
  if (!el) {
    el = document.createElement('style');
    el.id = 'wl-styles';
    document.head.appendChild(el);
  }

  el.textContent = `
    .bg-blue-600, .hover\\:bg-blue-700:hover { background-color: ${primary} !important; }
    .bg-blue-700 { background-color: ${pDark} !important; }
    .bg-blue-50, .hover\\:bg-blue-50:hover { background-color: ${pLight} !important; }
    .text-blue-600, .hover\\:text-blue-600:hover { color: ${primary} !important; }
    .text-blue-700 { color: ${pDark} !important; }
    .border-blue-500, .border-blue-600 { border-color: ${primary} !important; }
    .focus\\:ring-blue-500:focus { --tw-ring-color: ${primary} !important; }
    .border-l-4.border-blue-500 { border-left-color: ${primary} !important; }
    .accent-blue-600 { accent-color: ${primary} !important; }
  `;
};

const clearColors = () => {
  document.getElementById('wl-styles')?.remove();
};

const applyFavicon = (url: string) => {
  let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = url;
};

export const WhiteLabelProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [config, setConfig] = useState<WhiteLabelConfig | null>(null);

  const load = () => {
    if (!localStorage.getItem('access_token')) return;

    getWhiteLabelConfig()
      .then((result) => {
        if (!result.has_config || !result.config) {
          clearColors();
          setConfig(null);
          return;
        }
        const cfg = result.config;
        setConfig(cfg);
        applyColors(cfg.primary_color);
        if (cfg.app_name) document.title = cfg.app_name;
        if (cfg.favicon_url) applyFavicon(cfg.favicon_url);
      })
      .catch(() => {/* plan sans white-label, on ignore */});
  };

  useEffect(() => {
    load();
    return clearColors;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <WhiteLabelContext.Provider value={{ config, reload: load }}>
      {children}
    </WhiteLabelContext.Provider>
  );
};
